import { useEffect, useState } from 'react';
import type { MascotFace } from '../../../../../shared/ember-mascot.js';

/**
 * Ember under the tree (拍板 2026-09-14): the small warm presence lives on the
 * first screen, in the band between the canopy and the ground — never over the
 * figure, a light's tag or the composer. It keeps to itself: hides behind the
 * trunk, peeks out at the figure (at you), comes out and sits beside it to look
 * up at the lights, wanders round, comes a little closer to look at you, and
 * goes back behind the trunk. One round is about forty seconds and starts at a
 * random beat, so two visits never open on the same picture.
 *
 * Everything here is in scene units (BanyanScene: the ground is y=600, the
 * viewer sits at x=280, the trunk stands from x=170 to x=390 at its foot and
 * narrows going up). `x` is Ember's centre, `w` its width (its height is 1.2w;
 * the figure under the tree is 150–176 tall, so 80 keeps Ember a little over
 * half its size, 90 is as close as it comes). `front` says which side of the
 * trunk it is on: the scene paints the trunk over a back Ember and under a
 * front one, so walking behind the trunk is just walking. The layer only
 * changes while Ember stands clear of the trunk, where the swap shows nothing.
 *
 * Faces are the core ones — curious when it looks at you, listening when it
 * sits, thinking when it looks up, warm when it comes near — with one
 * exception the landing toy is allowed: a firefly lands on its tip and it is
 * surprised, then glad.
 */
export interface EmberPose {
  x: number;
  w: number;
  front: boolean;
  face: MascotFace;
  /** on its way: the small hop while the transform runs */
  walking: boolean;
  /** a short move (ducking back) runs on a quicker transition */
  quick: boolean;
}

interface Beat {
  id: string;
  pose: Omit<EmberPose, 'quick'>;
  ms: number;
  quick?: boolean;
  /** a second movement inside the beat, after `ms`: the layer swap happens here, standing clear of the trunk */
  then?: { pose: Partial<Omit<EmberPose, 'quick'>>; ms: number };
  firefly?: boolean;
}

/** How long a move takes (CSS transition on .banyan-ember). */
export const EMBER_MOVE_MS = 2600;
export const EMBER_QUICK_MS = 1400;

const HIDE: Omit<EmberPose, 'quick'> = { x: 280, w: 80, front: false, face: 'listening', walking: false };

export const EMBER_BEATS: readonly Beat[] = Object.freeze([
  { id: 'hide', pose: HIDE, ms: 2200 },
  { id: 'peek', pose: { x: 185, w: 80, front: false, face: 'curious', walking: false }, ms: 3600 },
  { id: 'duck', pose: HIDE, ms: 1400, quick: true },
  // out from behind the trunk's left side, then round to sit at the figure's left shoulder
  { id: 'out', pose: { x: 120, w: 80, front: false, face: 'warm', walking: true }, ms: EMBER_MOVE_MS, then: { pose: { x: 200, front: true, face: 'listening' }, ms: EMBER_MOVE_MS + 3000 } },
  { id: 'lights', pose: { x: 200, w: 80, front: true, face: 'thinking', walking: false }, ms: 4200, firefly: true },
  { id: 'cross', pose: { x: 430, w: 86, front: true, face: 'curious', walking: true }, ms: EMBER_MOVE_MS + 800 },
  { id: 'near', pose: { x: 470, w: 90, front: true, face: 'warm', walking: true }, ms: EMBER_MOVE_MS, then: { pose: { face: 'listening' }, ms: 3200 } },
  // back to the trunk's right side, clear of it, then behind
  { id: 'back', pose: { x: 440, w: 80, front: true, face: 'listening', walking: true }, ms: EMBER_MOVE_MS, then: { pose: { x: 280, front: false }, ms: EMBER_MOVE_MS } },
]);

/** Where Ember sits during the ceremony: at the figure's left shoulder, a little behind it. */
export const EMBER_CEREMONY_POSE: EmberPose = { x: 222, w: 84, front: true, face: 'listening', walking: false, quick: false };

/**
 * Ember's pose while a letter someone is waiting on lies sealed at the feet
 * (2026-09-29 拍板): in front, warm, still once it has arrived. Where it is
 * comes from the pile, not from here — it sits on the top envelope
 * (letter-pile.ts letterPerch; x and w below are not read), because the pile
 * keeps to the figure while the wander's places are squashed on a phone.
 */
export const EMBER_LETTERS_POSE: EmberPose = { x: 442, w: 84, front: true, face: 'warm', walking: false, quick: false };

/**
 * Every face Ember can wear under the tree: the beats' faces, the firefly's
 * surprised → glad, warm when held, and the ceremony's listening / warm /
 * thinking. The scene draws only these (mascotSvg `faces`) — the other faces
 * of the twenty are never shown here, and each undrawn face is a handful of
 * blurred shapes the browser no longer paints (2026-09-18).
 */
export const EMBER_TREE_FACES: readonly MascotFace[] = Object.freeze([...new Set<MascotFace>([
  ...EMBER_BEATS.flatMap((b) => [b.pose.face, ...(b.then?.pose.face ? [b.then.pose.face] : [])]),
  'surprised', 'glad', 'warm', 'listening', 'thinking',
])]);

/**
 * The wander, as a pose that changes on its own clock. `active=false` holds
 * the current pose (a tag is open, the letters are out, the person tapped
 * Ember); `held` is that last case — Ember stops and faces you (warm).
 * Reduced motion: Ember sits beside the figure and stays.
 */
export function useEmberWander({ active, held, enabled }: { active: boolean; held: boolean; enabled: boolean }): { pose: EmberPose; firefly: number } {
  const still = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * EMBER_BEATS.length));
  // go: the beat's first move is running · arrived: it is over, Ember holds ·
  // then: the second move is running · then-arrived: it is over, Ember holds
  const [step, setStep] = useState<'go' | 'arrived' | 'then' | 'then-arrived'>('go');
  const [firefly, setFirefly] = useState(0);
  const [override, setOverride] = useState<MascotFace | null>(null);
  const beat = EMBER_BEATS[idx];

  useEffect(() => {
    if (!enabled || !active || still) return undefined;
    const b = EMBER_BEATS[idx];
    const timers: number[] = [];
    const at = (ms: number, fn: () => void): void => { timers.push(window.setTimeout(fn, ms)); };
    setStep('go');
    setOverride(null);
    const moveMs = b.pose.walking ? (b.quick ? EMBER_QUICK_MS : EMBER_MOVE_MS) : 0;
    if (moveMs) at(moveMs, () => setStep('arrived'));
    if (b.firefly) {
      setFirefly((n) => n + 1);
      // the firefly lands on its tip at ~2s; it is surprised, then glad
      at(2000, () => setOverride('surprised'));
      at(3100, () => setOverride('glad'));
    }
    const next = (): void => { setOverride(null); setIdx((i) => (i + 1) % EMBER_BEATS.length); };
    if (b.then) {
      const then = b.then;
      at(b.ms, () => setStep('then'));
      if (then.pose.x !== undefined) at(b.ms + EMBER_MOVE_MS, () => setStep('then-arrived'));
      at(b.ms + then.ms, next);
    } else {
      at(b.ms, next);
    }
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [enabled, active, still, idx]);

  if (!enabled) return { pose: EMBER_CEREMONY_POSE, firefly: 0 };
  if (still) return { pose: { x: 200, w: 80, front: true, face: 'listening', walking: false, quick: false }, firefly: 0 };
  const inThen = (step === 'then' || step === 'then-arrived') && beat.then;
  const base = inThen ? { ...beat.pose, ...beat.then!.pose } : beat.pose;
  const walking = step === 'go' ? base.walking : step === 'then' ? beat.then?.pose.x !== undefined : false;
  const pose: EmberPose = { ...base, walking: held ? false : walking, face: held ? 'warm' : (override ?? base.face), quick: !!beat.quick && step === 'go' };
  return { pose, firefly };
}
